<template>
  <main :class="{ loading }">
    <Header>
      <router-link
        id="go-to-settings"
        :to="{ name: 'settings' }"
        class="header__action"
      >
        <IconSettings />
        Configurações
      </router-link>
      <router-link
        id="go-to-pages"
        :to="{ name: 'pagelist' }"
        class="header__action"
      >
        Páginas
      </router-link>
      <router-link
        id="go-to-modals"
        :to="{ name: 'modallist' }"
        class="header__action"
        :class="{ 'header__action--disabled': isModalList }"
      >
        Pop-ups
      </router-link>
      <router-link
        :to="{ name: 'metrics' }"
        class="header__action"
      >
        Métricas
      </router-link>
      <h2 class="hotel-title">
        {{ siteHostname }}
      </h2>
    </Header>
    <div class="instances">
      <div class="instances__management">
        <div class="instances__management__header">
          <h2>{{ $route.meta.label }}</h2>
          <button
            v-if="isModalList"
            class="btn btn-secondary btn-icon"
            :disabled="isViewerUser"
            @click="toggleTemplates"
          >
            <IconPlus size="xs" />
            <span> Criar pop-up </span>
          </button>
        </div>

        <div
          v-show="showTemplates"
          class="instances__templates"
        >
          <TemplateCard
            v-for="(template, index) in templateOptions"
            :key="index"
            v-bind="template"
            :selected="
              selectedTemplate && selectedTemplate.module === template.module
            "
            class="instances__templates__template"
            @click.native="selectTemplate(template)"
          />
        </div>

        <div
          v-if="parentInstanceList && parentInstanceList.length === 0"
          class="instances__empty-state"
        >
          Você ainda não criou nenhum pop-up 😿
        </div>

        <div
          v-else
          class="instances__table"
        >
          <div
            v-for="instanceId in parentInstanceList"
            :key="instanceId"
          >
            <div class="instances__table__row">
              <div class="instances__table__column-icon">
                <div>
                  <IconModule />
                </div>
              </div>

              <div
                class="instances__table__column-title"
                @click="goToEditor(instances[instanceId])"
              >
                <p>{{ instances[instanceId].title }}</p>
              </div>

              <div
                class="instances__table__column-title"
                @click="goToEditor(instances[instanceId])"
              >
                <p>{{ instanceName(instances[instanceId]) }}</p>
              </div>

              <div class="instances__table__column-buttons">
                <span
                  class="instance-status"
                  :class="{
                    'instance-status--unpublished':
                      !instances[instanceId].isPublished,
                    'instance-status--published':
                      instances[instanceId].isPublished &&
                      !isDirtyInstance(instances[instanceId]),
                    'instance-status--dirty':
                      instances[instanceId].isPublished &&
                      isDirtyInstance(instances[instanceId]),
                  }"
                >
                  {{ getInstanceStatus(instances[instanceId]) }}
                </span>

                <div
                  v-if="isModalList"
                  v-show="openedActionMenu === instanceId"
                  v-positioned
                  v-clickaway="closeActionMenu"
                  class="instances__action-menu"
                >
                  <ul>
                    <li>
                      <button
                        class="instances__action-menu__btn instances__action-menu__btn--goto"
                        @click.stop="goToEditor(instances[instanceId])"
                      >
                        Editar
                      </button>
                    </li>
                    <li v-show="instances[instanceId].isPublished">
                      <button
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="
                          setModalBeingUnpublished(instances[instanceId])
                        "
                      >
                        Desativar pop-up
                      </button>
                    </li>
                    <li v-show="!instances[instanceId].isPublished">
                      <button
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="
                          setModalBeingPublished(instances[instanceId])
                        "
                      >
                        Ativar pop-up
                      </button>
                    </li>
                    <li>
                      <button
                        v-show="
                          !modalHasTranslation(instances[instanceId], 'en')
                        "
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="
                          addTranslation(instances[instanceId], 'en')
                        "
                      >
                        Criar versão em inglês
                        <IconLanguage
                          language="en"
                          size="xs"
                          class="instances__action-menu__btn__flag"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        v-show="
                          !modalHasTranslation(instances[instanceId], 'es')
                        "
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="
                          addTranslation(instances[instanceId], 'es')
                        "
                      >
                        Criar versão em espanhol
                        <IconLanguage
                          language="es"
                          size="xs"
                          class="instances__action-menu__btn__flag"
                        />
                      </button>
                    </li>
                    <li>
                      <button
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="cloneModal(instances[instanceId])"
                      >
                        Duplicar pop-up
                      </button>
                    </li>
                    <li>
                      <button
                        class="instances__action-menu__btn"
                        :disabled="isViewerUser"
                        @click.stop="editModal(instances[instanceId])"
                      >
                        Alterar nome
                      </button>
                    </li>
                    <li>
                      <button
                        class="instances__action-menu__btn instances__action-menu__btn--remove"
                        :disabled="isViewerUser"
                        @click.stop="modalBeingDeleted = instances[instanceId]"
                      >
                        Excluir pop-up
                      </button>
                    </li>
                  </ul>
                </div>

                <button
                  v-if="isModalList"
                  class="instances__action-menu-opener"
                  :disabled="isViewerUser"
                  @click.stop="toggleActionMenu(instanceId)"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
            </div>

            <div
              v-for="translationId in instances[instanceId].translationIds"
              :key="translationId"
              class="instances__table__row"
            >
              <div class="instances__table__column-icon"></div>

              <div
                class="instances__table__column-title"
                @click="goToEditor(instances[translationId])"
              >
                <IconLanguage
                  :language="instances[translationId].language"
                  class="instances__table__column-title__flag"
                />
                <p>{{ instances[translationId].title }}</p>
              </div>

              <div
                class="instances__table__column-title"
                @click="goToEditor(instances[translationId])"
              >
                <p>{{ instanceName(instances[translationId]) }}</p>
              </div>

              <div class="instances__table__column-buttons">
                <span
                  class="instance-status"
                  :class="{
                    'instance-status--unpublished':
                      !instances[translationId].isPublished,
                    'instance-status--published':
                      instances[translationId].isPublished &&
                      !isDirtyInstance(instances[translationId]),
                    'instance-status--dirty':
                      instances[translationId].isPublished &&
                      isDirtyInstance(instances[translationId]),
                  }"
                >
                  {{ getInstanceStatus(instances[translationId]) }}
                </span>

                <div
                  v-if="isModalList"
                  v-show="openedActionMenu === translationId"
                  v-positioned
                  v-clickaway="closeActionMenu"
                  class="instances__action-menu"
                >
                  <ul>
                    <li>
                      <button
                        class="instances__action-menu__btn instances__action-menu__btn--goto"
                        @click.stop="goToEditor(instances[translationId])"
                      >
                        Editar
                      </button>
                    </li>
                    <li v-show="instances[translationId].isPublished">
                      <button
                        class="instances__action-menu__btn"
                        @click.stop="
                          setModalBeingUnpublished(instances[translationId])
                        "
                      >
                        Desativar pop-up
                      </button>
                    </li>
                    <li v-show="!instances[translationId].isPublished">
                      <button
                        class="instances__action-menu__btn"
                        @click.stop="
                          setModalBeingPublished(instances[translationId])
                        "
                      >
                        Ativar pop-up
                      </button>
                    </li>
                    <li>
                      <button
                        class="instances__action-menu__btn"
                        @click.stop="editModal(instances[translationId])"
                      >
                        Alterar nome
                      </button>
                    </li>
                    <li>
                      <button
                        class="instances__action-menu__btn instances__action-menu__btn--remove"
                        @click.stop="
                          modalBeingDeleted = instances[translationId]
                        "
                      >
                        Excluir pop-up
                      </button>
                    </li>
                  </ul>
                </div>

                <button
                  v-if="isModalList"
                  class="instances__action-menu-opener"
                  :disabled="isViewerUser"
                  @click.stop="toggleActionMenu(translationId)"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="isModalOpen"
        class="instances__add-modal"
        @click.self="resetModalEditing()"
      >
        <div class="instances__add-modal__content">
          <div class="instances__add-modal__content__header">
            <h2>{{ currentAction }} pop-up</h2>
            <label
              for="btn-close"
              @click="resetModalEditing()"
            >
              <IconClose />
            </label>
          </div>

          <form
            class="instances__add-modal__content__form"
            @submit.prevent="saveModal"
          >
            <div class="instances__add-modal__content__form__title">
              <label for="title">Nome</label>
              <input
                id="title"
                v-model.trim="modalTitle"
                type="text"
                autocomplete="off"
                placeholder="Escreva o nome"
                :class="{ 'is-invalid': hasError('title') }"
              />
              <p
                v-show="hasError('title')"
                class="invalid-feedback"
              >
                Por favor, preencha o nome do pop-up
              </p>
            </div>

            <div class="instances__add-modal__content__form__buttons">
              <button
                class="btn btn-secondary btn-white"
                type="button"
                @click="resetModalEditing()"
              >
                Cancelar
              </button>
              <button
                class="btn btn-secondary"
                type="submit"
              >
                {{ currentAction }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div
        v-show="isRemoveModalOpen"
        class="instances__remove-modal-modal"
        @click.self="modalBeingDeleted = null"
      >
        <div class="instances__remove-modal-modal__content">
          <div class="instances__remove-modal-modal__content__header">
            <label
              class="btn-close"
              @click="modalBeingDeleted = null"
            >
              <IconClose />
            </label>
            <IconDelete
              class="text-danger"
              size="lg"
            />
            <h2>Atenção</h2>
          </div>

          <div class="instances__remove-modal-modal__content__text">
            <p>
              Ao excluir, você perde todas as edições.
              <br />Tem certeza que deseja excluir esse pop-up?
            </p>
          </div>

          <div class="instances__remove-modal-modal__content__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="modalBeingDeleted = null"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="removeModal()"
            >
              Sim, quero excluir
            </button>
          </div>
        </div>
      </div>

      <!-- Modal "Desativar" -->
      <div
        v-show="isUnpublishModalOpen"
        class="publish-modal-modal"
        @click="setModalBeingUnpublished(null)"
      >
        <div
          class="publish-modal-modal__content"
          @click.stop
        >
          <div class="publish-modal-modal__content__header">
            <label
              class="btn-close"
              @click="setModalBeingUnpublished(null)"
            >
              <IconClose />
            </label>
            <IconWarning
              class="text-warning"
              size="lg"
            />
            <h2>Desativar</h2>
          </div>

          <div class="publish-modal-modal__content__text">
            <p>
              Ao <b>desativar</b> seu pop-up, ele não será mais exibido para
              seus usuários. Deseja continuar?
            </p>
          </div>

          <div class="publish-modal-modal__content__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="setModalBeingUnpublished(null)"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="unpublishModal()"
            >
              Sim, quero desativar!
            </button>
          </div>
        </div>
      </div>

      <!-- Modal "Ativar" -->
      <div
        v-show="isPublishModalOpen"
        class="publish-modal-modal"
        @click="setModalBeingPublished(null)"
      >
        <div
          class="publish-modal-modal__content"
          @click.stop
        >
          <div class="publish-modal-modal__content__header">
            <label
              class="btn-close"
              @click="setModalBeingPublished(null)"
            >
              <IconClose />
            </label>
            <IconRocket
              class="text-warning"
              size="lg"
            />
            <h2>Ativar</h2>
          </div>

          <div class="publish-modal-modal__content__text">
            <p>
              Ao <b>ativar</b> seu pop-up, ele será exibido nas páginas
              configuradas. Deseja continuar?
            </p>
          </div>

          <div class="publish-modal-modal__content__buttons">
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="setModalBeingPublished(null)"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="publishModal()"
            >
              Sim, quero ativar!
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import TemplateCard from '@/components/TemplateCard.vue';
import presets from '@/store/dicts/presets';
import modalPresets from '@/store/dicts/modalPresets';
import isPresetEnabled from '@/store/helpers/isPresetEnabled';

import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconRocket from '@/components/icons/IconRocket.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import IconModule from '@/components/icons/IconModule.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import IconWarning from '@/components/icons/IconWarning.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import { pushDataLayer } from '@/helpers/gtm';

export default {
  name: 'InstanceList',
  components: {
    Header,
    Footer,
    IconClose,
    IconRocket,
    IconSettings,
    IconModule,
    IconLanguage,
    IconDelete,
    IconWarning,
    IconPlus,
    TemplateCard,
  },
  directives: {
    positioned: {
      bind(el, binding, vnode) {
        /* eslint-disable no-param-reassign */
        el.positionByViewport = () => {
          const parentPosition = el.parentElement.getBoundingClientRect();
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
          if (parentPosition.bottom <= viewportHeight - 250) {
            el.style.top = '100%';
            el.style.bottom = 'unset';
          } else {
            el.style.bottom = '100%';
            el.style.top = 'unset';
          }
        };

        vnode.context.$nextTick(el.positionByViewport);
        window.addEventListener('scroll', el.positionByViewport);
      },
      unbind(el) {
        window.removeEventListener('scroll', el.positionByViewport);
      },
    },
    // clickaway taken from https://stackoverflow.com/questions/36170425/detect-click-outside-element
    clickaway: {
      bind(el, binding, vnode) {
        // eslint-disable-next-line no-param-reassign
        el.clickOutsideEvent = (event) => {
          // here I check that click was outside the el and his children
          if (!(el === event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      modalBeingDeleted: null,
      modalBeingEdited: null,
      modalBeingUnpublished: null,
      modalBeingPublished: null,
      modalTitle: '',
      isModalOpen: false,
      openedActionMenu: null,
      fieldsWithError: [],
      showTemplates: false,
      selectedTemplate: null,
    };
  },
  computed: {
    templateOptions() {
      const filteredTemplates = modalPresets.filter((preset) => {
        return isPresetEnabled(preset, this.$store.state, this.$store.getters);
      });

      return filteredTemplates;
    },
    currentAction() {
      return this.modalBeingEdited === null ? 'Criar' : 'Alterar';
    },
    isRemoveModalOpen() {
      return this.modalBeingDeleted !== null;
    },
    isUnpublishModalOpen() {
      return this.modalBeingUnpublished !== null;
    },
    isPublishModalOpen() {
      return this.modalBeingPublished !== null;
    },
    isValid() {
      return this.fieldsWithError.length === 0;
    },
    parentInstanceList() {
      return this.parentGlobalInstanceList;
    },
    instances() {
      return this.globalInstances;
    },
    isModalList() {
      return this.$route.name === 'modallist';
    },
    presets() {
      return this.isModalList ? modalPresets : presets;
    },
    ...mapState([
      'customer',
      'customerSettings',
      'globalInstances',
      'parentGlobalInstanceList',
      'loading',
    ]),
    ...mapGetters(['siteHostname', 'isViewerUser']),
  },
  methods: {
    instanceName(instance) {
      const module = this.presets.find(
        (preset) => preset.module === instance.module,
      );
      return module?.name || instance.name;
    },
    validate() {
      this.fieldsWithError = [];

      if (!/\S/.test(this.modalTitle)) {
        this.fieldsWithError.push('title');
      }
    },
    async saveModal() {
      this.validate();

      if (this.isValid) {
        const { data = {}, module = '' } = this.selectedTemplate || {};

        const modal = {
          title: this.modalTitle,
          data,
          module,
        };

        if (this.modalBeingEdited === null) {
          const newModal = await this.$store.dispatch('addGlobalInstance', {
            base: modal,
            endpoint: 'modals',
          });

          this.goToEditor(newModal);

          pushDataLayer({
            event: 'modal-created',
            module: this.selectedTemplate.name,
          });
        } else {
          const {
            modalBeingEdited: { redirect, ...modalBeingEdited },
          } = this;
          this.$store
            .dispatch('editGlobalInstanceBasicInfo', {
              id: modalBeingEdited.id,
              data: {
                title: this.modalTitle,
              },
              endpoint: 'modals',
            })
            .then(() => {
              if (redirect) {
                this.goToEditor({ ...modalBeingEdited, ...modal });
              }
            });

          this.toggleActionMenu(null);
        }

        this.resetModalEditing();
      }
    },
    resetModalEditing() {
      this.modalBeingEdited = null;
      this.modalTitle = '';
      this.isModalOpen = false;
      this.fieldsWithError = [];
      this.selectedTemplate = null;
    },
    editModal(modal) {
      this.modalBeingEdited = modal;
      this.modalTitle = modal.title;
      this.isModalOpen = true;
    },
    removeModal() {
      this.$store
        .dispatch('removeGlobalInstance', {
          id: this.modalBeingDeleted.id,
          endpoint: 'modals',
        })
        .then(() => {
          this.modalBeingDeleted = null;

          this.toggleActionMenu(null);
        });
    },
    goToEditor(instance) {
      this.$router.push({
        name: this.isModalList ? 'modaleditor' : 'globaleditor',
        params: instance,
      });
    },
    toggleActionMenu(instanceId) {
      if (this.openedActionMenu === instanceId) {
        this.openedActionMenu = null;
      } else {
        this.openedActionMenu = instanceId;
      }
    },
    closeActionMenu() {
      this.openedActionMenu = null;
    },
    hasError(field) {
      return this.fieldsWithError.includes(field);
    },
    async cloneModal(modal) {
      const newModal = await this.$store.dispatch('addGlobalInstance', {
        base: modal,
        endpoint: 'modals',
      });
      this.toggleActionMenu(null);
      this.editModal({ ...newModal, redirect: true });
      this.$store.dispatch('loadGlobals', { endpoint: 'modals' });
    },
    setModalBeingUnpublished(modalBeingUnpublished) {
      this.modalBeingUnpublished = modalBeingUnpublished;
    },
    setModalBeingPublished(modalBeingPublished) {
      this.modalBeingPublished = modalBeingPublished;
    },
    unpublishModal() {
      this.$store
        .dispatch('setGlobalInstanceStatus', {
          global: this.modalBeingUnpublished,
          isPublished: false,
          endpoint: 'modals',
        })
        .then(() => {
          this.setModalBeingUnpublished(null);
          this.toggleActionMenu(null);
        });
    },
    publishModal() {
      this.$store
        .dispatch('setGlobalInstanceStatus', {
          global: this.modalBeingPublished,
          isPublished: true,
          endpoint: 'modals',
        })
        .then(() => {
          this.setModalBeingPublished(null);
          this.toggleActionMenu(null);
        });
    },
    isDirtyInstance(instance) {
      // Handle legacy
      if (typeof instance.isDirty !== 'boolean') {
        return !instance.isPublished;
      }

      return instance.isDirty;
    },
    getInstanceStatus(instance) {
      if (!instance.isPublished) {
        return 'Desativado';
      }

      if (this.isDirtyInstance(instance)) {
        return 'Alterado';
      }

      return 'Ativado';
    },
    showModal() {
      this.isModalOpen = true;
    },
    toggleTemplates() {
      this.showTemplates = !this.showTemplates;
    },
    selectTemplate(template) {
      this.selectedTemplate = template;
      this.isModalOpen = true;
    },
    async addTranslation({ id: parentId, ...modal }, language) {
      const newModal = await this.$store.dispatch('addGlobalInstance', {
        base: {
          ...modal,
          parentId,
          language,
        },
        endpoint: 'modals',
      });
      this.toggleActionMenu(null);
      this.editModal({ ...newModal, redirect: true });
      this.$store.dispatch('loadGlobals', { endpoint: 'modals' });
    },
    modalHasTranslation(modal, language) {
      return (
        Array.isArray(modal.translations) &&
        modal.translations.includes(language)
      );
    },
  },
};
</script>

<style scoped lang="scss">
main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f2f2f2;
  margin: 0;
  padding-bottom: 45px; // footer height

  &.loading {
    opacity: 0.75;
    cursor: progress;

    > * {
      pointer-events: none;
    }
  }

  header {
    position: unset;
    width: unset;
  }
}

.instances {
  width: 100%;
  max-width: 1600px;
  flex-grow: 1;
  margin: 0 auto;
  padding: 32px 16px 24px;

  &__management {
    padding: 32px;
    background-color: $lb-white;

    h2 {
      font-size: $size-bigger;
      font-weight: $lb-medium;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .btn {
        height: 40px;
        padding: 4px 20px;
        font-size: $size-small;
      }
    }
  }

  &__empty-state {
    height: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: $size-normal;
  }

  &__table {
    width: 100%;

    &__row:nth-child(-n + 1) {
      border-top: 1px solid $lb-light-gray;
    }

    &__row {
      align-items: center;
      border-bottom: 1px solid $lb-light-gray;
      display: flex;
      height: 55px;
      justify-content: space-between;
      position: relative;
      width: 100%;

      &:hover {
        background-color: #f5f5f5;
      }
    }

    &__column-icon {
      height: 100%;
      width: 5%;
      display: flex;
      justify-content: center;
      align-items: center;

      div {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 20px;
        width: 30px;
      }
    }

    &__column-title {
      align-items: center;
      display: flex;
      height: 100%;
      padding: 0 5px 0 5px;
      flex: 1;
      cursor: pointer;

      &__flag {
        margin-right: 10px;
      }

      p {
        max-width: 90%;
        overflow: hidden;
        padding-right: 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &__column-buttons {
      height: 100%;
      width: 25%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 10px 0 10px;

      button {
        outline: none;
        border: none;
        background: none;
      }

      .instance-status {
        align-items: center;
        display: flex;

        &::before {
          border-radius: 1986px;
          content: '';
          display: inline-block;
          height: 8px;
          margin-right: 8px;
          width: 8px;
        }

        &--unpublished {
          &::before {
            background-color: #999592;
          }
        }

        &--published {
          &::before {
            background-color: #58f6a3;
          }
        }

        &--dirty {
          &::before {
            background-color: #7590f4;
          }
        }
      }

      .btn-modal-delete {
        color: $crm-red-primary;
      }
    }
  }

  &__action-menu {
    background-color: $lb-white;
    box-shadow: 4px 4px 25px rgba(0, 0, 0, 0.25);
    position: absolute;
    right: 0;
    z-index: 1;

    &-opener {
      color: inherit;
      height: 100%;
      padding: 1em;

      &[disabled] {
        opacity: 50%;
        cursor: not-allowed;
      }

      span {
        @include is-rounded;

        width: 4px;
        height: 4px;
        background: $crm-blue-primary;
        display: inline-block;
        margin: 0 2px;
      }
    }

    &__btn {
      color: inherit;
      padding: 0.5em 1.5em;
      width: 100%;
      text-align: left;

      &[disabled] {
        opacity: 0.5;
      }

      &--goto {
        padding-top: 1.5em;
      }

      &--remove {
        color: $crm-red-primary;
        padding-bottom: 1.5em;
      }

      &:hover {
        background-color: #f5f5f5;
        cursor: pointer;
      }
    }
  }

  &__add-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &__content {
      height: 400px;
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;

      &__header {
        display: flex;
        justify-content: space-between;
        width: 100%;

        h2 {
          font-size: $size-bigger;
        }

        label {
          height: 26px;
          width: 26px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: -16px -10px -20px 0px;
          cursor: pointer;
        }
      }

      &__form {
        width: 100%;
        height: 100%;
        padding: 20px 0px 20px 0px;
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .invalid-feedback {
          display: block;
        }

        input {
          border: none;
          border-bottom: 1px solid $lb-dark-gray;
          outline: none;
          padding-bottom: 6px;

          &:invalid,
          &.is-invalid {
            border-bottom: 2px solid #e0414b;
            box-shadow: none;
          }
        }

        &__title {
          display: flex;
          flex-direction: column;
          margin-bottom: -30px;

          .invalid-feedback {
            margin-bottom: 35px;
          }
        }

        &__url-path {
          display: flex;
          flex-direction: column;

          p {
            display: flex;
            align-items: center;

            input {
              margin-left: 14px;
              width: 100%;
            }
          }
        }

        &__buttons {
          display: flex;
          justify-content: space-between;

          button {
            min-width: 0px;
          }

          .btn-secondary {
            width: 40%;
          }

          .btn-white {
            width: 30%;
          }
        }
      }
    }
  }

  &__remove-modal-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;

        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }

        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;

        p {
          font-size: $size-normal;
          line-height: 26px;
          color: $gray-900;
          margin-bottom: 20px;
        }
      }

      &__buttons {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 70%;
      }
    }
  }

  &__templates {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: calc(40px - 1em);

    &__template {
      width: calc(25% - 16px);

      &::v-deep {
        img {
          aspect-ratio: 9 / 5;
        }
      }

      &:nth-child(4n) {
        margin-right: 0;
      }
    }
  }
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
}

.publish-modal-modal {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: rgba($lb-dark-gray, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9;

  &__content {
    width: 545px;
    display: flex;
    padding: 40px;
    flex-direction: column;
    background-color: $lb-white;
    align-items: center;

    &__header {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      flex-direction: column;
      position: relative;

      h2 {
        font-size: $size-bigger;
        font-weight: $lb-medium;
        line-height: 35px;
        text-align: center;
        letter-spacing: -1px;
        color: $gray-900;
        margin: 40px 0 20px 0;
      }

      .btn-close {
        position: absolute;
        top: 0;
        right: 0;
        cursor: pointer;
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      line-height: 26px;
      color: $gray-900;
      margin-bottom: 20px;

      &__no-errors {
        font-size: $size-normal;
      }

      &__has-errors {
        font-size: $size-small;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      width: 70%;
    }
  }
}
</style>
